import React from 'react';
import { Navigate } from 'react-router';
import {
  useAuthState, usePermissionsLoaded, useResetAuth, useSessionBootstrap,
} from '@/features/auth';

type Props = {
  children: React.ReactNode;
  /** Set to false on the workspace picker page itself, which can render without an active workspace.
   * Default true: redirects to `/workspaces` when no active app is set. */
  requireActiveApp?: boolean;
};

export function AuthGuard({ children, requireActiveApp = true }: Props): React.ReactNode {
  const { step, activeApp } = useAuthState();
  const permissionsLoaded = usePermissionsLoaded();
  const bootstrap = useSessionBootstrap();
  const resetAuth = useResetAuth();

  /* If the bootstrap query errors while we're authenticated but scopes haven't loaded yet
   * (post-login or post-workspace-switch), the user would otherwise be stuck on a blank
   * screen forever — `retry: false` prevents auto-recovery and `staleTime: Infinity` keeps
   * the cached error around. Match the pre-login failure path: clear the session and let
   * the next render redirect to /login so the user can re-authenticate. */
  React.useEffect(() => {
    if (step === 'authenticated' && !permissionsLoaded && bootstrap.isError) {
      resetAuth();
    }
  }, [step, permissionsLoaded, bootstrap.isError, resetAuth]);

  if (step !== 'authenticated') {
    // First page load: try to restore the session from the access_token cookie
    // before falling through to the login redirect.
    if (bootstrap.isLoading) return null;
    /* Bridge the one-frame gap between the query resolving and the effect that mirrors its
     * data into the auth store firing — without this guard, a successful hard-refresh
     * bootstrap renders <Navigate to="/login"> for the render between data arriving and the
     * effect promoting `step` to 'authenticated', causing a visible round-trip via /login. */
    if (bootstrap.data) return null;
    return <Navigate to="/login" replace />;
  }

  /* Authenticated but scopes/flags haven't been fetched yet (post-login flow, or workspace
   * switch). Block gated children until /me lands so <Gate> doesn't deny against an empty
   * scope list and flash the access-denied fallback. */
  if (!permissionsLoaded) return null;

  if (requireActiveApp && !activeApp) {
    return <Navigate to="/workspaces" replace />;
  }

  return <>{children}</>;
}
